import PropTypes from "prop-types";
import { useMemo } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TimelineItem from "examples/Timeline/TimelineItem";

function OrdersOverview({ contractInfo }) {
  // Calculer les notifications de vérification
  const notifications = useMemo(() => {
    if (!contractInfo) return [];

    const calculateDaysRemaining = () => {
      const referenceDate = contractInfo.dateVerif || contractInfo.depart;
      if (!referenceDate) return null;

      const [day, month, year] = referenceDate.split("/");
      const refDate = new Date(year, month - 1, day);
      const nextVerifDate = new Date(refDate);
      nextVerifDate.setDate(nextVerifDate.getDate() + 30);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      nextVerifDate.setHours(0, 0, 0, 0);

      const diffTime = nextVerifDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return { days: diffDays, nextDate: nextVerifDate };
    };

    const result = calculateDaysRemaining();
    if (!result) return [];

    const { days, nextDate } = result;
    const alerts = [];

    // Format de la date de prochaine vérification
    const dateStr = nextDate.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });

    if (days < 0) {
      // EN RETARD - Urgent
      alerts.push({
        color: "error",
        icon: "warning",
        title: `⚠️ RETARD de vérification - ${contractInfo.immat}`,
        description: `${Math.abs(days)} jour(s) de retard`,
        dateTime: `Attendu le ${dateStr}`,
        priority: 1,
      });
    } else if (days === 0) {
      // AUJOURD'HUI
      alerts.push({
        color: "warning",
        icon: "today",
        title: `🚨 Vérification AUJOURD'HUI - ${contractInfo.immat}`,
        description: `Vérification à effectuer`,
        dateTime: dateStr,
        priority: 2,
      });
    } else if (days <= 3) {
      // TRÈS PROCHE - Dans 1-3 jours
      alerts.push({
        color: "warning",
        icon: "notification_important",
        title: `⏰ Vérification dans ${days} jour(s) - ${contractInfo.immat}`,
        description: `À prévoir rapidement`,
        dateTime: dateStr,
        priority: 3,
      });
    } else if (days <= 7) {
      // PROCHE - Dans 4-7 jours
      alerts.push({
        color: "info",
        icon: "event",
        title: `📅 Vérification dans ${days} jours - ${contractInfo.immat}`,
        description: `Prévoir la vérification`,
        dateTime: dateStr,
        priority: 4,
      });
    } else {
      // OK - Plus de 7 jours
      alerts.push({
        color: "success",
        icon: "check_circle",
        title: `✅ Vérification OK - ${contractInfo.immat}`,
        description: `${days} jours restants`,
        dateTime: `Prochaine le ${dateStr}`,
        priority: 5,
      });
    }

    // Ajouter une notification sur la dernière vérification
    if (contractInfo.dateVerif) {
      alerts.push({
        color: "dark",
        icon: "history",
        title: `Dernière vérification - ${contractInfo.immat}`,
        description: `${contractInfo.kmVERIF?.toLocaleString() || "N/A"} km`,
        dateTime: contractInfo.dateVerif,
        priority: 6,
      });
    }

    return alerts.sort((a, b) => a.priority - b.priority);
  }, [contractInfo]);

  // Calculer le statut global
  const overallStatus = useMemo(() => {
    if (notifications.length === 0) return { text: "Aucune donnée", color: "text" };

    const topNotif = notifications[0];
    if (topNotif.color === "error") return { text: "Action requise", color: "error" };
    if (topNotif.color === "warning") return { text: "À surveiller", color: "warning" };
    if (topNotif.color === "info") return { text: "Normal", color: "info" };
    return { text: "Tout va bien", color: "success" };
  }, [notifications]);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox pt={3} px={3}>
        <MDTypography variant="h6" fontWeight="medium">
          Notifications Vérification
        </MDTypography>
        <MDBox mt={0} mb={2}>
          <MDTypography variant="button" color={overallStatus.color} fontWeight="medium">
            <Icon fontSize="small" sx={{ mr: 0.5 }}>
              {overallStatus.color === "error" ? "error" :
               overallStatus.color === "warning" ? "warning" :
               "info"}
            </Icon>
            {overallStatus.text}
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox p={2}>
        {notifications.length > 0 ? (
          notifications.map((notif, index) => (
            <TimelineItem
              key={index}
              color={notif.color}
              icon={notif.icon}
              title={notif.title}
              description={notif.description}
              dateTime={notif.dateTime}
              lastItem={index === notifications.length - 1}
            />
          ))
        ) : (
          <MDBox textAlign="center" py={3}>
            <MDTypography variant="button" color="text">
              Aucune donnée de vérification disponible
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

OrdersOverview.propTypes = {
  contractInfo: PropTypes.shape({
    immat: PropTypes.string,
    depart: PropTypes.string,
    dateVerif: PropTypes.string,
    kmVERIF: PropTypes.number,
  }),
};

OrdersOverview.defaultProps = {
  contractInfo: null,
};

export default OrdersOverview;
